import { faChevronLeft, faChevronRight, faSearchPlus } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { ReactImageGalleryItem } from "react-image-gallery";

import { GalleryPhotoOrigin } from "@/mk2/apps/wiki/components/GalleryPhotoOrigin";
import { Props } from "@/mk2/apps/wiki/containers/ArticleExperiencePhoto/ArticleExperiencePhotoPage";
import {
    ArticleGalleryItem,
    buildArticleGalleryPhotos,
    galleryItemLinkTo,
    isMergedScope,
} from "@/mk2/apps/wiki/gallery";
import { wikiArticleExperiencePhotoUrl } from "@/mk2/apps/wiki/urls";
import { ImageGalleryLoadable } from "@/mk2/components/ImageGalleryLoadable";
import { LoadingSwitch } from "@/mk2/containers/LoadingSwitch/LoadingSwitch";
import { cacheLast } from "@/mk2/helpers/cache";
import { url } from "@/mk2/helpers/urls";
import { photoUrl } from "@/mk/photo/photoUrl";

import styles from "./ArticleExperiencePhoto.mscss";
import "./ArticleExperiencePhoto.scss";

export class ArticleExperiencePhoto extends React.Component<Props> {
    private galleryItems = cacheLast<ArticleGalleryItem[]>(true);

    public render() {
        const { loadingState, article } = this.props;

        return (
            <div className={styles.ArticleExperiencePhoto}>
                <LoadingSwitch
                    loadingState={loadingState}
                    onLoad={this.load}
                    onRenderSuccess={this.renderSuccess}
                    hasData={!!article}
                />
            </div>
        );
    }

    // Reached from the merged product row? Then navigate the whole merged gallery
    // (article + all approved experience photos). Reached from a single review's photo
    // row? Then stay scoped to that one experience (its own photos only).
    private isMerged(): boolean {
        return isMergedScope(this.props.location.search);
    }

    private getGalleryItems(): ArticleGalleryItem[] {
        const { article, experience, experiencePhoto } = this.props;
        const merged = this.isMerged();
        return this.galleryItems(
            () => {
                const scopedItems: ArticleGalleryItem[] = (experience.photos ?? []).map((p) => ({
                    photofile: p.photofile,
                    experienceId: experience.id,
                }));
                if (!merged) {
                    return scopedItems;
                }
                const items = buildArticleGalleryPhotos(article);
                // A still-pending experience linked with the merged flag won't be in the
                // public merged list — fall back to its own photos so the link still works.
                return items.some((item) => item.photofile.id === experiencePhoto.photofile.id)
                    ? items
                    : scopedItems;
            },
            merged,
            article.photos,
            article.experiences,
            experience.photos,
        );
    }

    private renderSuccess = () => {
        const { experiencePhoto } = this.props;
        const items = this.getGalleryItems();

        const currentIndex: number = items.findIndex(
            (item) => item.photofile.id === experiencePhoto.photofile.id,
        );

        const galleryPhotos: ReactImageGalleryItem[] = items.map(({ photofile }) => ({
            original: photoUrl(photofile, "s738x738"),
            thumbnail: photoUrl(photofile, "s150x150"),
            fullSizeUrl: photoUrl(photofile, "s1600x1600"),
        }));

        return (
            <>
                <div className={styles.ArticleExperiencePhoto__gallery}>
                    <ImageGalleryLoadable
                        items={galleryPhotos}
                        lazyLoad
                        showIndex
                        showThumbnails={false}
                        autoPlay={false}
                        showPlayButton={false}
                        showFullscreenButton={false}
                        slideDuration={500}
                        startIndex={currentIndex}
                        onSlide={this.onSlide}
                        renderLeftNav={this.renderLeftNav}
                        renderRightNav={this.renderRightNav}
                        renderCustomControls={this.renderCustomControls}
                    />
                </div>
            </>
        );
    };

    private renderLeftNav = (onClick, disabled) => {
        return (
            <div
                key="prev"
                className={styles.ArticleExperiencePhoto__prev}
                onClick={disabled ? undefined : onClick}
            >
                <FontAwesomeIcon
                    icon={faChevronLeft}
                    className={styles.ArticleExperiencePhoto__prev__icon}
                />
            </div>
        );
    };

    private renderRightNav = (onClick, disabled) => {
        return (
            <div
                key="next"
                className={styles.ArticleExperiencePhoto__next}
                onClick={disabled ? undefined : onClick}
            >
                <FontAwesomeIcon
                    icon={faChevronRight}
                    className={styles.ArticleExperiencePhoto__next__icon}
                />
            </div>
        );
    };

    private renderCustomControls = () => {
        const { article, photoId } = this.props;
        const item = this.getGalleryItems().find((i) => i.photofile.id === photoId);
        if (!item) {
            return null;
        }
        const showMagnifier = item.photofile?.versions?.includes("s1600x1600");

        return (
            <>
                <GalleryPhotoOrigin article={article} item={item} />
                {showMagnifier && (
                    <div className={styles.ArticleExperiencePhoto__controls}>
                        <a
                            data-google-interstitial="false"
                            key="magnifier"
                            href={photoUrl(item.photofile, "s1600x1600")}
                            target="_blank"
                            rel="noopener"
                            className={styles.ArticleExperiencePhoto__controls__control}
                        >
                            <FontAwesomeIcon icon={faSearchPlus} />
                        </a>
                    </div>
                )}
            </>
        );
    };

    private onSlide = (currentIndex: number) => {
        const { article, routeNavigate, routeParams } = this.props;
        const item = this.getGalleryItems()[currentIndex];
        if (!item) {
            return;
        }
        if (this.isMerged()) {
            routeNavigate(galleryItemLinkTo(article, item), { replace: true });
            return;
        }
        routeNavigate(
            url(wikiArticleExperiencePhotoUrl, {
                categorySlug: routeParams.categorySlug,
                articleSlug: routeParams.articleSlug,
                experienceId: routeParams.experienceId,
                photoId: item.photofile.id,
            }),
            { replace: true },
        );
    };

    private load = () => {
        const {
            routeParams: { categorySlug, articleSlug },
            experienceId,
            photoId,
            onLoad,
        } = this.props;
        onLoad(articleSlug, categorySlug, experienceId, photoId);
    };
}
